import { useRef, useState } from 'react';
import axios from 'axios';
import { Upload, X, Image as ImageIcon } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const MAX_WIDTH = 1600;
const MAX_SIZE_MB = 1;

const compressImage = (file) => {
  return new Promise((resolve) => {
    if (file.size <= MAX_SIZE_MB * 1024 * 1024) {
      resolve(file);
      return;
    }
    const img = new window.Image();
    const canvas = document.createElement('canvas');
    const reader = new FileReader();

    reader.onload = (e) => {
      img.onload = () => {
        let { width, height } = img;
        if (width > MAX_WIDTH) {
          height = (height * MAX_WIDTH) / width;
          width = MAX_WIDTH;
        }
        canvas.width = width;
        canvas.height = height;
        const ctx = canvas.getContext('2d');
        ctx.drawImage(img, 0, 0, width, height);
        canvas.toBlob(
          (blob) => {
            const compressed = new File([blob], file.name, { type: 'image/jpeg' });
            resolve(compressed);
          },
          'image/jpeg',
          0.82
        );
      };
      img.src = e.target.result;
    };
    reader.readAsDataURL(file);
  });
};

const ImageUpload = ({ value, onChange }) => {
  const [dragging, setDragging] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState('');
  const inputRef = useRef(null);

  const upload = async (file) => {
    if (!file) return;
    setUploading(true);
    setError('');
    try {
      const compressed = await compressImage(file);
      const formData = new FormData();
      formData.append('file', compressed);
      const { data } = await axios.post(`${API}/upload`, formData);
      onChange(data.url);
    } catch (err) {
      const msg = err.response?.data?.detail || err.message || 'Errore nel caricamento';
      setError(msg);
    } finally {
      setUploading(false);
    }
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setDragging(false);
    const file = e.dataTransfer.files[0];
    if (file) upload(file);
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) upload(file);
  };

  if (value) {
    return (
      <div className="relative rounded-xl overflow-hidden border border-border" data-testid="image-preview">
        <img src={value} alt="Copertina" className="w-full h-48 object-cover" />
        <button
          type="button"
          onClick={() => onChange('')}
          className="absolute top-2 right-2 bg-black/60 text-white rounded-full p-1.5 hover:bg-black/80 transition-colors"
          data-testid="remove-image-btn"
        >
          <X size={14} />
        </button>
        <button
          type="button"
          onClick={() => inputRef.current?.click()}
          className="absolute bottom-2 right-2 bg-black/60 text-white text-xs rounded-lg px-3 py-1.5 hover:bg-black/80 transition-colors"
        >
          Cambia
        </button>
        <input ref={inputRef} type="file" accept="image/*" className="hidden" onChange={handleFileChange} />
      </div>
    );
  }

  return (
    <div
      onDrop={handleDrop}
      onDragOver={(e) => { e.preventDefault(); setDragging(true); }}
      onDragLeave={() => setDragging(false)}
      onClick={() => inputRef.current?.click()}
      className={`cursor-pointer rounded-xl border-2 border-dashed transition-all duration-200 flex flex-col items-center justify-center gap-3 p-10 ${
        dragging
          ? 'border-primary bg-primary/5 scale-[1.01]'
          : 'border-border hover:border-primary/50 hover:bg-muted/50'
      }`}
      data-testid="image-dropzone"
    >
      <input
        ref={inputRef}
        type="file"
        accept="image/*"
        className="hidden"
        onChange={handleFileChange}
      />

      {uploading ? (
        <>
          <div className="w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin" />
          <p className="text-sm text-foreground/60">Caricamento in corso...</p>
        </>
      ) : (
        <>
          <div className="w-12 h-12 rounded-full bg-muted flex items-center justify-center">
            {dragging ? <Upload size={22} className="text-primary" /> : <ImageIcon size={22} className="text-foreground/40" />}
          </div>
          <div className="text-center">
            <p className="text-sm font-medium text-foreground/70">
              {dragging ? 'Rilascia per caricare' : 'Trascina qui la foto o clicca per selezionarla'}
            </p>
            <p className="text-xs text-foreground/40 mt-1">JPG, PNG, WebP</p>
          </div>
        </>
      )}

      {error && <p className="text-xs text-red-500 mt-2" data-testid="upload-error">{error}</p>}
    </div>
  );
};

export default ImageUpload;
